import React, { useCallback, useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import './GstPstAgentPanel.css';

/**
 * GST & PST sub-auditor panel.
 *
 * Tabs:
 *   1. Overview      — registration cards, next-deadline countdown, current-period status
 *   2. Returns       — list of reporting periods with computed GST34 lines + status
 *   3. Prepare       — full GST34 worksheet, ITC entry, NETFILE walkthrough, mark as filed
 *   4. PST Monitor   — BC PST position, registration trigger rules
 *   5. Guide         — plain-language first-time filing process (served from GST_PST_GUIDE.md)
 *
 * Backend: /api/opo/gst-pst/*
 */

const TABS = [
  { id: 'overview',  label: 'Overview'     },
  { id: 'returns',   label: 'Returns'      },
  { id: 'prepare',   label: 'Prepare'      },
  { id: 'pst',       label: 'PST Monitor'  },
  { id: 'guide',     label: 'Guide'        },
];

function StatusBadge({ status }) {
  const map = {
    filed:     { label: 'Filed',     cls: 'filed'    },
    'due-soon': { label: 'Due Soon',  cls: 'due-soon' },
    overdue:   { label: 'Overdue',   cls: 'overdue'  },
    open:      { label: 'Open',      cls: 'open'     },
    future:    { label: 'Upcoming',  cls: 'future'   },
  };
  const s = map[status] || { label: status, cls: 'open' };
  return <span className={`gst-badge gst-badge--${s.cls}`}>{s.label}</span>;
}

function DaysCountdown({ days }) {
  if (days == null) return null;
  const cls = days < 0 ? 'overdue' : days <= 14 ? 'urgent' : days <= 45 ? 'soon' : 'ok';
  const label = days < 0
    ? `${Math.abs(days)} days overdue`
    : days === 0
      ? 'Due today'
      : `${days} days remaining`;
  return <span className={`gst-countdown gst-countdown--${cls}`}>{label}</span>;
}

function Line({ number, label, amount }) {
  return (
    <div className="gst-ws-line">
      <span className="gst-ws-line-num">Line {number}</span>
      <span className="gst-ws-line-label">{label}</span>
      <span className="gst-ws-line-amount">{amount != null ? `$${Number(amount).toFixed(2)}` : '—'}</span>
    </div>
  );
}

function VerificationBadge({ state }) {
  const map = {
    complete: { label: 'Complete', cls: 'filed' },
    pending_pdf: { label: 'PDF needed', cls: 'due-soon' },
    pending_ledger: { label: 'Record conf. #', cls: 'open' },
    none: { label: 'Not filed', cls: 'future' },
  };
  const s = map[state] || map.none;
  return <span className={`gst-badge gst-badge--${s.cls}`}>{s.label}</span>;
}

// ── Overview Tab ─────────────────────────────────────────────────────────────

function OverviewTab({ summary, onGoToPrepare, onGoToReturns }) {
  if (!summary) return <div className="gst-loading">Loading…</div>;
  const {
    gst, currentPeriod, lastFiled, nextDeadline, daysToDeadline, computedLines,
    instalmentRequired, filedCount, periodCount, completeCount, pendingPdfCount,
    proactive, periodTimeline,
  } = summary;

  const bannerCls = proactive?.level === 'critical' ? 'gst-proactive-banner--critical'
    : proactive?.level === 'warning' ? 'gst-proactive-banner--warning'
      : 'gst-proactive-banner--ok';

  return (
    <div className="gst-overview">
      {proactive && (
        <div className={`gst-proactive-banner ${bannerCls}`}>
          <div className="gst-proactive-headline">{proactive.headline}</div>
          <p className="gst-proactive-body">{proactive.body}</p>
          {proactive.action === 'prepare_return' && (
            <button type="button" className="gst-btn gst-btn--primary" onClick={onGoToPrepare}>Prepare return →</button>
          )}
          {proactive.action === 'upload_pdf' && (
            <button type="button" className="gst-btn gst-btn--primary" onClick={onGoToPrepare}>Upload PDF →</button>
          )}
        </div>
      )}

      <div className="gst-cards-row">
        <div className="gst-card gst-card--registered">
          <div className="gst-card-header">
            <span className="gst-card-icon">🏛</span>
            <span className="gst-card-title">GST Registration</span>
            <span className="gst-badge gst-badge--filed">Active</span>
          </div>
          <div className="gst-card-body">
            <div className="gst-kv"><span>Account</span><code>{gst.account}</code></div>
            <div className="gst-kv"><span>Effective</span><span>{gst.effectiveDate}</span></div>
            <div className="gst-kv"><span>Frequency</span><span className="gst-cap">{gst.filingFrequency}</span></div>
            <div className="gst-kv"><span>Returns on file</span><span>{completeCount} complete · {filedCount} filed · {periodCount} total</span></div>
            {pendingPdfCount > 0 && (
              <div className="gst-kv gst-kv--warn"><span>Action</span><span>{pendingPdfCount} PDF upload pending</span></div>
            )}
          </div>
        </div>
        {lastFiled && (
          <div className="gst-card gst-card--filed">
            <div className="gst-card-header">
              <span className="gst-card-icon">✓</span>
              <span className="gst-card-title">Last Filed</span>
              <VerificationBadge state={lastFiled.verification?.state} />
            </div>
            <div className="gst-card-body">
              <div className="gst-kv"><span>Period</span><span>{lastFiled.label}</span></div>
              <div className="gst-kv"><span>Filed</span><span>{lastFiled.filing?.filed_date}</span></div>
              <div className="gst-kv"><span>Confirmation</span><code>#{lastFiled.filing?.confirmation_number}</code></div>
              {lastFiled.verification?.pdf?.viewUrl && (
                <a href={lastFiled.verification.pdf.viewUrl} target="_blank" rel="noopener noreferrer" className="gst-btn gst-btn--secondary gst-btn--link">View PDF</a>
              )}
            </div>
          </div>
        )}
        {currentPeriod && (
          <div className={`gst-card gst-card--deadline${daysToDeadline != null && daysToDeadline <= 45 ? ' gst-card--urgent' : ''}`}>
            <div className="gst-card-header">
              <span className="gst-card-icon">📅</span>
              <span className="gst-card-title">Next Due</span>
              <StatusBadge status={currentPeriod.status} />
            </div>
            <div className="gst-card-body">
              <div className="gst-kv"><span>Period</span><span>{currentPeriod.label}</span></div>
              <div className="gst-kv"><span>Due</span><strong>{nextDeadline}</strong></div>
              <div className="gst-kv"><span>Time left</span><DaysCountdown days={daysToDeadline} /></div>
              <div className="gst-kv"><span>Projected L109</span><span>${(currentPeriod.lines?.line109 || 0).toFixed(2)}</span></div>
            </div>
          </div>
        )}
      </div>

      {periodTimeline?.length > 0 && (
        <div className="gst-section">
          <div className="gst-section-title">Filing status — all periods</div>
          <div className="gst-timeline">
            {periodTimeline.map((p) => (
              <div key={p.id} className={`gst-timeline-row gst-timeline-row--${p.status}`}>
                <div className="gst-timeline-main">
                  <span className="gst-timeline-label">{p.label}</span>
                  <span className="gst-timeline-meta">Due {p.due}{p.status !== 'filed' && p.daysToDue != null ? ` · ${p.daysToDue} days` : ''}</span>
                  {p.confirmationNumber && <span className="gst-timeline-conf">Conf. #{p.confirmationNumber}</span>}
                </div>
                <div className="gst-timeline-badges">
                  <StatusBadge status={p.status} />
                  <VerificationBadge state={p.verification} />
                </div>
              </div>
            ))}
          </div>
          <button type="button" className="gst-btn gst-btn--secondary" onClick={onGoToReturns}>View all returns →</button>
        </div>
      )}

      {currentPeriod && computedLines && (
        <div className="gst-section">
          <div className="gst-section-title">Open period worksheet — {currentPeriod.label}</div>
          <div className="gst-ws-mini">
            <Line number="101" label="Taxable supplies" amount={computedLines.line101} />
            <Line number="105" label="GST collected" amount={computedLines.line105} />
            <Line number="108" label="Input tax credits" amount={computedLines.line108} />
            <div className="gst-ws-divider" />
            <Line number="109" label="Net tax" amount={computedLines.line109} />
          </div>
          {instalmentRequired && (
            <div className="gst-info-banner gst-info-banner--warning">
              Annual net tax ≥ $3,000 — quarterly instalments may be required (ETA s.237).
            </div>
          )}
          <button type="button" className="gst-btn gst-btn--primary" onClick={onGoToPrepare}>
            Prepare this return →
          </button>
        </div>
      )}
    </div>
  );
}

// ── Returns Tab ───────────────────────────────────────────────────────────────

function ReturnsTab({ periods, onSelectPeriod }) {
  if (!periods) return <div className="gst-loading">Loading…</div>;
  return (
    <div className="gst-returns">
      <p className="gst-section-desc">
        GST34 reporting periods for OptiPrime AI Inc. Annual filer — FYE April 30.
        Stub period Apr 2026 filed (conf. #749698). Next annual return due <strong>2027-07-31</strong>.
      </p>
      <div className="gst-periods-list">
        {periods.map((p) => (
          <div key={p.id} className={`gst-period-row gst-period-row--${p.status}`}>
            <div className="gst-period-info">
              <div className="gst-period-label">{p.label}</div>
              <div className="gst-period-dates">{p.from} → {p.to} · due {p.due}</div>
              {p.filing && (
                <div className="gst-period-filed">
                  Filed {p.filing.filed_date} · Conf. #{p.filing.confirmation_number}
                  {p.verification?.pdf?.viewUrl && (
                    <> · <a href={p.verification.pdf.viewUrl} target="_blank" rel="noopener noreferrer">PDF</a></>
                  )}
                </div>
              )}
              {p.verification?.state === 'pending_pdf' && (
                <div className="gst-period-filed gst-period-filed--warn">Upload CRA confirmation PDF</div>
              )}
            </div>
            <div className="gst-period-amounts">
              <span className="gst-period-net">
                Line 109: <strong>${(p.lines?.line109 || 0).toFixed(2)}</strong>
                {p.lines?.isNil && <span className="gst-nil-tag">NIL</span>}
              </span>
              <StatusBadge status={p.status} />
              <VerificationBadge state={p.verification?.state} />
            </div>
            {p.status !== 'filed' ? (
              <button
                type="button"
                className="gst-btn gst-btn--secondary"
                onClick={() => onSelectPeriod(p)}
              >
                {p.status === 'future' ? 'Preview' : 'Prepare'}
              </button>
            ) : p.verification?.state === 'pending_pdf' ? (
              <button type="button" className="gst-btn gst-btn--secondary" onClick={() => onSelectPeriod(p)}>Upload PDF</button>
            ) : null}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Prepare Tab ───────────────────────────────────────────────────────────────

function PrepareTab({ periods, initialPeriod, onDataChange }) {
  const [selectedPeriodId, setSelectedPeriodId] = useState('');
  const [returnData, setReturnData] = useState(null);
  const [loadingReturn, setLoadingReturn] = useState(false);
  const [itcs, setItcs] = useState([]);
  const [loadingItcs, setLoadingItcs] = useState(false);
  const [newItc, setNewItc] = useState({ vendor: '', description: '', supply_date: '', invoice_number: '', amount: '', currency: 'CAD', notes: '' });
  const [addingItc, setAddingItc] = useState(false);
  const [itcError, setItcError] = useState('');
  const [filingForm, setFilingForm] = useState({ confirmation_number: '', filed_date: '', remitted_amount: '', notes: '' });
  const [markingFiled, setMarkingFiled] = useState(false);
  const [filingResult, setFilingResult] = useState(null);
  const [filingError, setFilingError] = useState('');
  const [showNetfileGuide, setShowNetfileGuide] = useState(false);
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const [uploadMsg, setUploadMsg] = useState('');
  const [uploadError, setUploadError] = useState('');

  useEffect(() => {
    if (!periods?.length) return;
    if (initialPeriod?.id) {
      setSelectedPeriodId(initialPeriod.id);
      return;
    }
    const pendingPdf = periods.find((p) => p.verification?.state === 'pending_pdf');
    const firstOpen = periods.find((p) => p.status !== 'filed');
    const pick = pendingPdf || firstOpen || periods[0];
    if (pick) setSelectedPeriodId(pick.id);
  }, [periods, initialPeriod?.id]);

  useEffect(() => {
    if (!selectedPeriodId) return;
    setLoadingReturn(true);
    setReturnData(null);
    fetch(`/api/opo/gst-pst/periods/${encodeURIComponent(selectedPeriodId)}/return`)
      .then((r) => r.json())
      .then((d) => { setReturnData(d); setLoadingReturn(false); })
      .catch(() => setLoadingReturn(false));
  }, [selectedPeriodId]);

  const loadItcs = useCallback(() => {
    setLoadingItcs(true);
    fetch('/api/opo/gst-pst/itcs')
      .then((r) => r.json())
      .then((d) => { setItcs(d.itcs || []); setLoadingItcs(false); })
      .catch(() => setLoadingItcs(false));
  }, []);

  useEffect(() => { loadItcs(); }, [loadItcs]);

  const handleAddItc = async () => {
    setItcError('');
    if (!newItc.vendor || !newItc.supply_date || !newItc.amount) {
      setItcError('Vendor, supply date, and amount are required.');
      return;
    }
    setAddingItc(true);
    try {
      const r = await fetch('/api/opo/gst-pst/itcs', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(newItc) });
      if (!r.ok) throw new Error((await r.json()).error || 'Failed');
      setNewItc({ vendor: '', description: '', supply_date: '', invoice_number: '', amount: '', currency: 'CAD', notes: '' });
      loadItcs();
    } catch (e) { setItcError(e.message); }
    setAddingItc(false);
  };

  const handleDeleteItc = async (id) => {
    await fetch(`/api/opo/gst-pst/itcs/${id}`, { method: 'DELETE' });
    loadItcs();
  };

  const handleMarkFiled = async () => {
    setFilingError('');
    if (!filingForm.confirmation_number || !filingForm.filed_date) {
      setFilingError('Confirmation number and filed date are required.');
      return;
    }
    setMarkingFiled(true);
    try {
      const r = await fetch(`/api/opo/gst-pst/periods/${encodeURIComponent(selectedPeriodId)}/mark-filed`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(filingForm),
      });
      if (!r.ok) throw new Error((await r.json()).error || 'Failed');
      const d = await r.json();
      setFilingResult(d.filing);
      setFilingForm({ confirmation_number: '', filed_date: '', remitted_amount: '', notes: '' });
      onDataChange?.();
    } catch (e) { setFilingError(e.message); }
    setMarkingFiled(false);
  };

  const handleUploadPdf = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedPeriodId) return;
    setUploadError('');
    setUploadMsg('');
    setUploadingPdf(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await fetch(`/api/opo/gst-pst/periods/${encodeURIComponent(selectedPeriodId)}/upload-return`, {
        method: 'POST',
        body: fd,
      });
      if (!r.ok) throw new Error((await r.json()).error || 'Upload failed');
      const d = await r.json();
      setUploadMsg(d.message || 'PDF saved.');
      onDataChange?.();
      fetch(`/api/opo/gst-pst/periods/${encodeURIComponent(selectedPeriodId)}/return`)
        .then((res) => res.json())
        .then(setReturnData);
    } catch (err) {
      setUploadError(err.message);
    }
    setUploadingPdf(false);
    e.target.value = '';
  };

  const period = periods?.find((p) => p.id === selectedPeriodId);
  const isAlreadyFiled = period?.status === 'filed';
  const verification = returnData?.period?.verification || period?.verification;

  return (
    <div className="gst-prepare">
      <div className="gst-prepare-period-select">
        <label className="gst-label" htmlFor="gst-period-select">Reporting period</label>
        <select
          id="gst-period-select"
          className="gst-select"
          value={selectedPeriodId}
          onChange={(e) => { setSelectedPeriodId(e.target.value); setFilingResult(null); setFilingError(''); }}
        >
          {(periods || []).map((p) => (
            <option key={p.id} value={p.id}>{p.label} · due {p.due}</option>
          ))}
        </select>
      </div>

      {loadingReturn && <div className="gst-loading">Loading worksheet…</div>}

      {returnData && !loadingReturn && (
        <>
          {/* GST34 Worksheet */}
          <div className="gst-section">
            <div className="gst-section-title">GST34 Worksheet</div>
            <div className="gst-section-desc">{returnData.period?.label} · Due {returnData.period?.due}</div>
            <div className="gst-ws">
              <div className="gst-ws-header">
                <span>Line</span><span>Description</span><span>Amount (CAD)</span>
              </div>
              <Line number="101" label="Total taxable supplies (revenue, excl. GST)" amount={returnData.lines?.line101} />
              <Line number="105" label="GST/HST collected or collectible" amount={returnData.lines?.line105} />
              <Line number="108" label="Total input tax credits (ITCs)" amount={returnData.lines?.line108} />
              <div className="gst-ws-divider" />
              <Line number="109" label="Net tax (Line 105 − Line 108)" amount={returnData.lines?.line109} />
            </div>
            <div className={`gst-net-position ${returnData.lines?.isNil ? 'gst-net-position--nil' : returnData.lines?.isRefund ? 'gst-net-position--refund' : 'gst-net-position--owing'}`}>
              {returnData.netPosition}
            </div>
            {returnData.instalmentNote && (
              <div className="gst-info-banner">{returnData.instalmentNote}</div>
            )}
          </div>

          {/* Source invoices */}
          {returnData.sourceInvoices?.length > 0 && (
            <div className="gst-section">
              <div className="gst-section-title">Source invoices ({returnData.sourceInvoices.length})</div>
              <div className="gst-source-table">
                <div className="gst-source-header">
                  <span>Invoice</span><span>Date</span><span>Subtotal</span><span>GST</span>
                </div>
                {returnData.sourceInvoices.map((inv) => (
                  <div key={inv.invoice_number} className="gst-source-row">
                    <span>{inv.invoice_number}</span>
                    <span>{inv.invoice_date}</span>
                    <span>${Number(inv.subtotal_excl_tax || 0).toFixed(2)}</span>
                    <span>${Number(inv.gst_amount || 0).toFixed(2)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ITC Ledger */}
          <div className="gst-section">
            <div className="gst-section-title">Input Tax Credits (Line 108)</div>
            <div className="gst-section-desc">
              Record GST paid to Canadian-registered suppliers. The supplier's RT number must appear on their invoice (ETA s.169(4)(a)).
            </div>
            {loadingItcs ? (
              <div className="gst-loading">Loading ITCs…</div>
            ) : itcs.length === 0 ? (
              <div className="gst-empty">No ITC entries. Current position: $0.00.</div>
            ) : (
              <div className="gst-source-table">
                <div className="gst-source-header">
                  <span>Vendor</span><span>Date</span><span>Inv #</span><span>Amount</span><span></span>
                </div>
                {itcs.map((itc) => (
                  <div key={itc.id} className="gst-source-row">
                    <span>{itc.vendor}</span>
                    <span>{itc.supply_date}</span>
                    <span>{itc.invoice_number || '—'}</span>
                    <span>${Number(itc.amount).toFixed(2)} {itc.currency}</span>
                    <span>
                      <button type="button" className="gst-btn-icon gst-btn-icon--danger" onClick={() => handleDeleteItc(itc.id)} title="Remove ITC">✕</button>
                    </span>
                  </div>
                ))}
              </div>
            )}

            {/* Add ITC form */}
            <div className="gst-itc-form">
              <div className="gst-form-row">
                <div className="gst-form-field">
                  <label className="gst-label">Vendor *</label>
                  <input className="gst-input" value={newItc.vendor} onChange={(e) => setNewItc({ ...newItc, vendor: e.target.value })} placeholder="e.g. Bell Canada" />
                </div>
                <div className="gst-form-field">
                  <label className="gst-label">Supply date *</label>
                  <input className="gst-input" type="date" value={newItc.supply_date} onChange={(e) => setNewItc({ ...newItc, supply_date: e.target.value })} />
                </div>
                <div className="gst-form-field">
                  <label className="gst-label">Invoice #</label>
                  <input className="gst-input" value={newItc.invoice_number} onChange={(e) => setNewItc({ ...newItc, invoice_number: e.target.value })} placeholder="INV-001" />
                </div>
                <div className="gst-form-field gst-form-field--sm">
                  <label className="gst-label">GST Amount (CAD) *</label>
                  <input className="gst-input" type="number" min="0" step="0.01" value={newItc.amount} onChange={(e) => setNewItc({ ...newItc, amount: e.target.value })} placeholder="0.00" />
                </div>
              </div>
              <div className="gst-form-row">
                <div className="gst-form-field gst-form-field--wide">
                  <label className="gst-label">Description</label>
                  <input className="gst-input" value={newItc.description} onChange={(e) => setNewItc({ ...newItc, description: e.target.value })} placeholder="Business expense description" />
                </div>
                <div className="gst-form-field gst-form-field--wide">
                  <label className="gst-label">Notes</label>
                  <input className="gst-input" value={newItc.notes} onChange={(e) => setNewItc({ ...newItc, notes: e.target.value })} placeholder="Supplier RT number, etc." />
                </div>
              </div>
              {itcError && <div className="gst-error">{itcError}</div>}
              <button type="button" className="gst-btn gst-btn--secondary" onClick={handleAddItc} disabled={addingItc}>
                {addingItc ? 'Adding…' : '+ Add ITC entry'}
              </button>
            </div>
          </div>

          {/* NETFILE Guide */}
          <div className="gst-section">
            <button
              type="button"
              className="gst-collapsible-header"
              onClick={() => setShowNetfileGuide((v) => !v)}
            >
              <span className="gst-section-title">CRA NETFILE Walkthrough</span>
              <span className="gst-collapsible-arrow">{showNetfileGuide ? '▲' : '▼'}</span>
            </button>
            {showNetfileGuide && (
              <div className="gst-netfile-steps">
                <div className="gst-step"><span className="gst-step-num">1</span><div><strong>Log in to CRA My Business Account</strong><br /><a href="https://www.canada.ca/en/revenue-agency/services/e-services/digital-services-businesses/business-account.html" target="_blank" rel="noopener noreferrer">canada.ca → My Business Account</a>. Select BN 792814030.</div></div>
                <div className="gst-step"><span className="gst-step-num">2</span><div><strong>Navigate to GST/HST → Account 792814030RT0001</strong><br />Click "File a return" for the correct period.</div></div>
                <div className="gst-step"><span className="gst-step-num">3</span><div><strong>Enter the line amounts above</strong><br />Copy Lines 101, 105, 108, 109 exactly as computed in the worksheet.</div></div>
                <div className="gst-step"><span className="gst-step-num">4</span><div><strong>Review and submit</strong><br />Confirm the net position, then click Submit (NETFILE). CRA will display a confirmation number.</div></div>
                <div className="gst-step"><span className="gst-step-num">5</span><div><strong>Save and record</strong><br />Download the filed return PDF. Upload below or save as <code>taxes/filings/gst_hst/gst_return_{returnData.period?.to}.pdf</code>. Record the confirmation number below.</div></div>
              </div>
            )}
          </div>

          {/* Mark as Filed */}
          {!isAlreadyFiled && !filingResult && (
            <div className="gst-section gst-section--mark-filed">
              <div className="gst-section-title">Mark as Filed</div>
              <div className="gst-form-row">
                <div className="gst-form-field">
                  <label className="gst-label">CRA Confirmation # *</label>
                  <input className="gst-input" value={filingForm.confirmation_number} onChange={(e) => setFilingForm({ ...filingForm, confirmation_number: e.target.value })} placeholder="e.g. 20260731-XXXXX" />
                </div>
                <div className="gst-form-field">
                  <label className="gst-label">Filed date *</label>
                  <input className="gst-input" type="date" value={filingForm.filed_date} onChange={(e) => setFilingForm({ ...filingForm, filed_date: e.target.value })} />
                </div>
                <div className="gst-form-field gst-form-field--sm">
                  <label className="gst-label">Remitted amount (CAD)</label>
                  <input className="gst-input" type="number" min="0" step="0.01" value={filingForm.remitted_amount} onChange={(e) => setFilingForm({ ...filingForm, remitted_amount: e.target.value })} placeholder={returnData.lines?.line109?.toFixed(2) || '0.00'} />
                </div>
              </div>
              <div className="gst-form-row">
                <div className="gst-form-field gst-form-field--wide">
                  <label className="gst-label">Notes</label>
                  <input className="gst-input" value={filingForm.notes} onChange={(e) => setFilingForm({ ...filingForm, notes: e.target.value })} placeholder="Any additional notes…" />
                </div>
              </div>
              {filingError && <div className="gst-error">{filingError}</div>}
              <button type="button" className="gst-btn gst-btn--success" onClick={handleMarkFiled} disabled={markingFiled}>
                {markingFiled ? 'Recording…' : '✓ Mark as Filed'}
              </button>
            </div>
          )}

          {isAlreadyFiled && (
            <div className="gst-info-banner gst-info-banner--success">
              This return has been filed
              {period?.filing ? ` — conf. #${period.filing.confirmation_number} on ${period.filing.filed_date}` : ''}.
            </div>
          )}

          {filingResult && (
            <div className="gst-info-banner gst-info-banner--success">
              ✓ Filed and recorded. Confirmation #{filingResult.confirmation_number} · {filingResult.filed_date}. Audit trail saved to ledger.
            </div>
          )}

          {(isAlreadyFiled || filingResult) && (
            <div className="gst-section gst-section--filing-record">
              <div className="gst-section-title">CRA confirmation PDF</div>
              <p className="gst-section-desc">
                Upload the PDF saved from CRA NETFILE (confirmation screen or &quot;Save as PDF&quot;).
                Stored at <code>taxes/filings/gst_hst/gst_return_{returnData?.period?.to || period?.to}.pdf</code>.
              </p>
              {verification?.state === 'complete' && verification?.pdf?.viewUrl ? (
                <div className="gst-info-banner gst-info-banner--success">
                  ✓ PDF verified on file —{' '}
                  <a href={verification.pdf.viewUrl} target="_blank" rel="noopener noreferrer">View filed return</a>
                </div>
              ) : (
                <div className="gst-upload-zone">
                  <label className="gst-upload-label">
                    <input
                      type="file"
                      accept="application/pdf,.pdf"
                      onChange={handleUploadPdf}
                      disabled={uploadingPdf}
                    />
                    <span className="gst-upload-icon">📄</span>
                    <span className="gst-upload-text">
                      {uploadingPdf ? 'Uploading…' : 'Choose PDF or drag here'}
                    </span>
                    <span className="gst-upload-hint">CRA NETFILE confirmation · PDF only</span>
                  </label>
                  {uploadError && <div className="gst-error">{uploadError}</div>}
                  {uploadMsg && <div className="gst-info-banner gst-info-banner--success">{uploadMsg}</div>}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── PST Monitor Tab ───────────────────────────────────────────────────────────

function PstMonitorTab({ pstStatus }) {
  if (!pstStatus) return <div className="gst-loading">Loading…</div>;
  return (
    <div className="gst-pst-monitor">
      <div className="gst-cards-row">
        <div className="gst-card">
          <div className="gst-card-header">
            <span className="gst-card-icon">🏔</span>
            <span className="gst-card-title">BC PST Status</span>
            <span className="gst-badge gst-badge--open">Not Registered</span>
          </div>
          <div className="gst-card-body">
            <div className="gst-kv"><span>PST collected</span><strong>${Number(pstStatus.totalPstCollected).toFixed(2)}</strong></div>
            <div className="gst-kv"><span>Current position</span><span>{pstStatus.currentPosition}</span></div>
            <div className="gst-kv"><span>BC PST rate</span><span>7%</span></div>
          </div>
        </div>
      </div>

      <div className="gst-section">
        <div className="gst-section-title">Registration Trigger Rules</div>
        <div className="gst-triggers">
          {(pstStatus.triggers || []).map((t) => (
            <div key={t.id} className={`gst-trigger ${t.isOk ? 'gst-trigger--ok' : t.triggered ? 'gst-trigger--active' : 'gst-trigger--watch'}`}>
              <div className="gst-trigger-header">
                <span className="gst-trigger-icon">{t.isOk ? '✓' : t.triggered ? '⚠️' : '👁'}</span>
                <span className="gst-trigger-label">{t.label}</span>
                {t.isOk ? <span className="gst-badge gst-badge--filed">Exempt</span> : t.triggered ? <span className="gst-badge gst-badge--overdue">Action Required</span> : <span className="gst-badge gst-badge--future">Watch</span>}
              </div>
              <p className="gst-trigger-desc">{t.description}</p>
              {t.action && <div className="gst-trigger-action"><strong>Action:</strong> {t.action}</div>}
            </div>
          ))}
        </div>
      </div>

      <div className="gst-section">
        <div className="gst-section-title">How to Register for BC PST</div>
        <p className="gst-section-desc">Register <em>before</em> issuing your first taxable supply (API billing, SaaS subscriptions, software licences).</p>
        <div className="gst-netfile-steps">
          <div className="gst-step"><span className="gst-step-num">1</span><div>Go to <a href="https://www.etaxbc.ca/" target="_blank" rel="noopener noreferrer">eTaxBC</a> and click "Register a new business for BC taxes".</div></div>
          <div className="gst-step"><span className="gst-step-num">2</span><div>Select PST as the tax type. Enter OptiPrime AI Inc. details and BN 792814030.</div></div>
          <div className="gst-step"><span className="gst-step-num">3</span><div>Note your PST Registration Number and add it to all invoices for taxable supplies.</div></div>
          <div className="gst-step"><span className="gst-step-num">4</span><div>File PST returns via eTaxBC on the schedule assigned (monthly or quarterly based on PST collected).</div></div>
        </div>
        <a href="https://www.etaxbc.ca/" target="_blank" rel="noopener noreferrer" className="gst-btn gst-btn--secondary gst-btn--external">
          Open eTaxBC →
        </a>
      </div>
    </div>
  );
}

// ── Guide Tab ─────────────────────────────────────────────────────────────────

function GuideTab({ guide }) {
  if (!guide) return <div className="gst-loading">Loading guide…</div>;
  return (
    <div className="gst-guide">
      <ReactMarkdown>{guide}</ReactMarkdown>
    </div>
  );
}

// ── Main Panel ────────────────────────────────────────────────────────────────

const GstPstAgentPanel = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [summary, setSummary] = useState(null);
  const [periods, setPeriods] = useState(null);
  const [pstStatus, setPstStatus] = useState(null);
  const [guide, setGuide] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState(null);

  const refreshData = useCallback(() => {
    fetch('/api/opo/gst-pst/summary').then((r) => r.json()).then(setSummary).catch(console.error);
    fetch('/api/opo/gst-pst/periods').then((r) => r.json()).then((d) => setPeriods(d.periods)).catch(console.error);
  }, []);

  useEffect(() => {
    refreshData();
    fetch('/api/opo/gst-pst/pst-status').then((r) => r.json()).then(setPstStatus).catch(console.error);
    fetch('/api/opo/gst-pst/guide').then((r) => r.json()).then((d) => setGuide(d.guide)).catch(console.error);
  }, [refreshData]);

  const handleGoToPrepare = useCallback(() => {
    setActiveTab('prepare');
  }, []);

  const handleGoToReturns = useCallback(() => {
    setActiveTab('returns');
  }, []);

  const handleSelectPeriod = useCallback((p) => {
    setSelectedPeriod(p);
    setActiveTab('prepare');
  }, []);

  const alertIcon = summary?.proactive?.level === 'critical' ? '🚨'
    : summary?.proactive?.level === 'warning' ? '⚠️' : '✓';

  return (
    <div className="gst-panel">
      <div className="gst-panel-header">
        <div className="gst-panel-title-row">
          <span className="gst-panel-icon">📊</span>
          <span className="gst-panel-title">GST &amp; PST</span>
          <span className="gst-panel-subtitle">Federal GST/HST · BC PST · CRA NETFILE</span>
        </div>
        {summary?.proactive && (
          <div className={`gst-panel-alert gst-panel-alert--${summary.proactive.level}`}>
            <span>{alertIcon}</span>
            <span>
              {summary.proactive.headline}
              {summary.daysToDeadline != null && summary.proactive.action === 'prepare_return' && (
                <> — due {summary.nextDeadline} · <DaysCountdown days={summary.daysToDeadline} /></>
              )}
            </span>
          </div>
        )}
      </div>

      <div className="gst-tabs" role="tablist">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={activeTab === t.id}
            className={`gst-tab${activeTab === t.id ? ' gst-tab--active' : ''}`}
            onClick={() => setActiveTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="gst-tab-content">
        {activeTab === 'overview'  && <OverviewTab summary={summary} onGoToPrepare={handleGoToPrepare} onGoToReturns={handleGoToReturns} />}
        {activeTab === 'returns'   && <ReturnsTab periods={periods} onSelectPeriod={handleSelectPeriod} />}
        {activeTab === 'prepare'   && <PrepareTab periods={periods} initialPeriod={selectedPeriod} onDataChange={refreshData} />}
        {activeTab === 'pst'       && <PstMonitorTab pstStatus={pstStatus} />}
        {activeTab === 'guide'     && <GuideTab guide={guide} />}
      </div>
    </div>
  );
};

export default GstPstAgentPanel;
